import { useTheme } from '@emotion/react'
import loadable from '@loadable/component'
import {
    AppState,
    CommentEvent,
    ConfigurationContext,
    EntitledToView,
    Product,
    useFeature,
} from '@news-mono/web-common'
import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { MessageBanner } from '../banners/MessageBanner/MessageBanner'
import { ContentSeparator } from '../content/ContentSeparator/ContentSeparator.styled'
import { StyledVideoErrorBoundary } from '../content/Video/Video.styled'
import { LoggerContext } from '../diagnostics/LoggerContext'
import { ErrorBoundary } from '../errors/ErrorBoundary/ErrorBoundary'
import { createRenderTarget } from '../render-target'
import { TextLink } from '../typography/TextLink/TextLink'
import { AuthenticationProviderContext } from '../user-registration/authentication/AuthenticationProvider'
import { useSectionMetaInfo } from '../__helpers'
import { ThemeMargins } from '../__styling/settings/metrics'
import {
    StyledAccountBox,
    StyledAccountText,
    StyledActionBox,
    StyledCommentIcon,
    StyledLinkButton,
    StyledRegisterBox,
    StyledRegisterText,
} from './Coral.styled'
import CoralComments from './Coral.web'

export const LazyCoral = loadable(() => import('./Coral.web'))

const CoralRender = createRenderTarget('Coral', {
    web: LazyCoral,
    amp: null,
    rss: null,
    preview: null,
    app: LazyCoral,
})

export interface CoralProps {
    storyID?: string
    storyURL?: string
    articleCommentsEnabled?: boolean
    horizontalSpacing?: keyof ThemeMargins
    onEvent: (event: CommentEvent) => void
}

export function getCoralStoryId(
    section: string,
    publicHostname: string,
    articleId: string,
) {
    return section !== 'default'
        ? `${publicHostname}-${section}-${articleId}`
        : `${publicHostname}-${articleId}`
}

export const Coral: React.FC<CoralProps> = ({
    storyID,
    storyURL,
    articleCommentsEnabled,
    horizontalSpacing,
    onEvent,
}) => {
    const { coralUrl, publicUrl } = useContext(ConfigurationContext)
    const logger = useContext(LoggerContext)

    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )

    const sectionUrl = `https://${useSectionMetaInfo().Hostname}`

    const { subscriptionType, isLoggedIn, isEntitledChecked } = useSelector(
        (state: AppState) => state.authentication,
    )

    const authState = useSelector((state: AppState) => state.authentication)

    const { kind } = useTheme()
    const areCommentsEnabled = useFeature('comments')

    const canShowComments =
        areCommentsEnabled &&
        articleCommentsEnabled &&
        (kind === Product.TheWest || kind === Product.PerthNow)
    subscriptionType !== 'corporate-site'

    const authContext = useContext(AuthenticationProviderContext)

    const [storyUrlLoaded, setStoryUrlLoaded] = useState(false)
    const [storyUrl, setStoryUrl] = useState('')

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setStoryUrl(window.location.href.split('?')[0].split('#')[0])
            setStoryUrlLoaded(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEntitledChecked])

    if (!canShowComments || typeof coralUrl === 'undefined') {
        return null
    }

    switch (kind) {
        case Product.PerthNow: {
            return (
                <React.Fragment>
                    <ContentSeparator
                        verticalBottomSpacing={'sm'}
                        verticalTopSpacing={'lg'}
                    />
                    {isLoggedIn ? (
                        <CoralRender
                            coralScriptUrl={coralUrl}
                            storyID={storyID}
                            onEvent={onEvent}
                            horizontalSpacing={horizontalSpacing}
                            hostName={publicUrl}
                            useContentSeperator={false}
                            storyURL={storyURL}
                        />
                    ) : (
                        <React.Fragment>
                            <StyledActionBox>
                                <StyledRegisterBox>
                                    <StyledCommentIcon />
                                    <StyledRegisterText>
                                        Register and have your say.
                                    </StyledRegisterText>
                                    <StyledLinkButton
                                        onClick={
                                            authContext &&
                                            authContext.onRegisterClick
                                        }
                                    >
                                        Register to comment
                                    </StyledLinkButton>
                                </StyledRegisterBox>
                                <StyledAccountBox>
                                    <StyledAccountText>
                                        Already have an account?
                                    </StyledAccountText>
                                    <StyledLinkButton
                                        onClick={
                                            authContext &&
                                            authContext.onLoginClick
                                        }
                                    >
                                        Log in
                                    </StyledLinkButton>
                                </StyledAccountBox>
                            </StyledActionBox>
                            <CoralRender
                                coralScriptUrl={coralUrl}
                                storyID={storyID}
                                onEvent={onEvent}
                                horizontalSpacing={horizontalSpacing}
                                hostName={publicUrl}
                                useContentSeperator={false}
                                storyURL={storyURL}
                            />
                        </React.Fragment>
                    )}
                </React.Fragment>
            )
        }
        default: {
            return storyUrlLoaded ? (
                <EntitledToView
                    requiredAccess={{ level: 'subscriber' }}
                    entitled={() => (
                        <ErrorBoundary
                            log={logger}
                            renderError={() => (
                                <StyledVideoErrorBoundary>
                                    <p>
                                        An error has occurred with loading
                                        comments for this article.
                                    </p>
                                </StyledVideoErrorBoundary>
                            )}
                        >
                            {/* Opt to not lazily load this for debugging INC-251 */}
                            <CoralComments
                                coralScriptUrl={coralUrl}
                                storyID={storyID}
                                onEvent={onEvent}
                                horizontalSpacing={horizontalSpacing}
                                hostName={publicUrl}
                                useContentSeperator={true}
                                storyURL={storyURL ? storyURL : storyUrl}
                            />
                        </ErrorBoundary>
                    )}
                    notEntitled={() => (
                        <React.Fragment>
                            <MessageBanner
                                icon="comment"
                                id="coral_thread"
                                onEvent={onEvent}
                            >
                                <p>
                                    To comment on this story and join the
                                    conversation, subscribe to The West
                                    Australian’s{' '}
                                    <TextLink href="https://subscriber.thewest.com.au/subscribenow/digital?utm_source=TheWest&utm_medium=Article-Block&utm_campaign=Comments&utm_term=subscribe&utm_content=login">
                                        Everyday Digital package
                                    </TextLink>
                                    .
                                </p>
                            </MessageBanner>
                            <ContentSeparator verticalBottomSpacing="lg" />
                        </React.Fragment>
                    )}
                />
            ) : (
                <></>
            )
        }
    }
}
